import { useState } from 'react';
import { formatUsd } from '../../lib/format';
import { AddSerialsDialog } from './AddSerialsDialog';

export interface IncludedLicense {
  partNumber: string;
  description: string;
  quantity: number;
}

/**
 * SIL-1648: summary of a stock line (quantity > 1) on the asset detail page:
 * quantity, line value, included licenses, and the admin-only "Add serials"
 * action. Also lists included licenses for a single unit when it has any.
 * Renders nothing for a plain single unit with no licenses.
 */
export function StockLineCard({
  assetId,
  quantity,
  lineValueUsd,
  includedLicenses,
  canEdit,
}: {
  assetId: string;
  quantity: number;
  lineValueUsd: number | null;
  includedLicenses: IncludedLicense[] | null;
  canEdit: boolean;
}) {
  const [open, setOpen] = useState(false);
  const isLine = quantity > 1;
  const licenses = includedLicenses ?? [];
  if (!isLine && licenses.length === 0) return null;

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3" data-testid="stock-line-card">
      {isLine && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Stock line</p>
            <p className="mt-1 text-sm text-foreground">
              <span className="font-semibold">Qty {quantity.toLocaleString()}</span>
              <span className="text-muted-foreground"> - no serial numbers yet</span>
            </p>
            <p className="mt-1 text-sm text-foreground">
              Line value{' '}
              <span className="font-num font-semibold">
                {lineValueUsd === null ? 'Not priced' : formatUsd(lineValueUsd)}
              </span>
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
            >
              Add serials
            </button>
          )}
        </div>
      )}
      {licenses.length > 0 && (
        <div className={isLine ? 'mt-3 border-t border-border pt-3' : ''}>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Included licenses</p>
          <ul className="mt-1 space-y-1 text-sm text-foreground">
            {licenses.map((l) => (
              <li key={l.partNumber}>
                <span className="font-mono text-xs">{l.partNumber}</span> {l.description}
                <span className="text-muted-foreground"> x {l.quantity.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {open && <AddSerialsDialog assetId={assetId} lineQuantity={quantity} onClose={() => setOpen(false)} />}
    </div>
  );
}
